import moment from 'moment'
import { CANCELLATION_POLICIES, TEN_DAYS_BEFORE_CHECK_IN } from 'constants/cancellationPolicies'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { getCalendarDateStringInTimeZone } from 'lib/datetime/dateUtils'
import { isNonNullable, min } from 'lib/array/arrayUtils'
import { mapObject } from 'lib/object/objectUtils'

export function roundToNine(amount: number): number {
  const rounded = Math.round(amount)
  const lower = Math.floor((rounded + 1) / 10) * 10 - 1
  const upper = lower + 10
  if (lower < 0) {
    return upper
  }
  return rounded - lower < upper - rounded ? lower : upper
}

export function applyCancellationOptionPriceFactor(amount: number, factor?: number | null): number {
  if (!amount || factor === undefined || factor === null || factor === 1) {
    return amount
  }
  return roundToNine(amount * factor)
}

export function applyCancellationOptionPriceFactorToPrices<T extends { price: number, memberPrice: number }>(
  prices: T,
  factor?: number | null,
): T {
  if (factor === undefined || factor === null || factor === 1) {
    return prices
  }
  return {
    ...prices,
    price: applyCancellationOptionPriceFactor(prices.price, factor),
    memberPrice: applyCancellationOptionPriceFactor(prices.memberPrice, factor),
  }
}

interface HotelPricedRow {
  hotelPrice: number
  hotelMemberPrice: number
  hotelTotal: number
  hotelMemberTotal: number
}

export function applyCancellationOptionPriceFactorToHotelPrices<T extends HotelPricedRow>(
  row: T,
  factor?: number | null,
): T {
  if (factor === undefined || factor === null || factor === 1) {
    return row
  }
  const hotelPrice = applyCancellationOptionPriceFactor(row.hotelPrice, factor)
  const hotelMemberPrice = applyCancellationOptionPriceFactor(row.hotelMemberPrice, factor)
  return {
    ...row,
    hotelPrice,
    hotelMemberPrice,
    hotelTotal: row.hotelTotal + hotelPrice - row.hotelPrice,
    hotelMemberTotal: row.hotelMemberTotal + hotelMemberPrice - row.hotelMemberPrice,
  }
}

export function isInsideCancellationOptionCutoff(checkIn?: string, timeZone?: string, now: Date = new Date()): boolean {
  if (!checkIn) {
    return false
  }
  const today = timeZone ? getCalendarDateStringInTimeZone(now, timeZone) : moment(now).format(ISO_DATE_FORMAT)
  return moment(checkIn).diff(moment(today), 'days') <= TEN_DAYS_BEFORE_CHECK_IN
}

function applyCancellationOptionToCalendarDay(day: App.CalendarDay, factor?: number, timeZone?: string): App.CalendarDay {
  const pricedDay = applyCancellationOptionPriceFactorToHotelPrices(day, factor)
  return isInsideCancellationOptionCutoff(day.checkIn, timeZone) ? { ...pricedDay, hotelAvailable: false } : pricedDay
}

function applyCancellationOptionToCalendar(calendar: App.Calendar, factor?: number, timeZone?: string): App.Calendar {
  const months = calendar.months.map((month): App.CalendarMonth => {
    const days = month.days.map(day => applyCancellationOptionToCalendarDay(day, factor, timeZone))
    return {
      ...month,
      days,
      cheapestDay: min(days.filter(d => d.hotelAvailable), d => d.hotelTotal),
      cheapestDayWithFlights: min(days.filter(d => d.flightsAvailable && d.hotelAvailable), d => d.packagedTotal),
      hotelAvailable: days.some(d => d.hotelAvailable),
    }
  })
  return {
    ...calendar,
    months,
    cheapestDay: min(months.map(m => m.cheapestDay).filter(isNonNullable), d => d.hotelTotal),
    cheapestDayWithFlights: min(months.map(m => m.cheapestDayWithFlights).filter(isNonNullable), d => d.packagedTotal),
  }
}

export function applyCancellationOptionToCalendars(
  calendars: Record<string, App.Calendar>,
  orderItem?: Pick<App.OrderItem, 'cancellationOptionPolicy' | 'cancellationOptionPriceFactor'>,
  timeZone?: string,
): Record<string, App.Calendar> {
  if (!orderItem?.cancellationOptionPolicy) {
    return calendars
  }
  return mapObject(calendars, calendar => calendar && applyCancellationOptionToCalendar(calendar, orderItem.cancellationOptionPriceFactor, timeZone))
}

export function isPackageOpenToBookingChange(
  pkg: Pick<App.HotelPackage, 'cancellationOptionPolicy' | 'roomRate'>,
  orderItem?: Pick<App.OrderItem, 'cancellationOptionPolicy'>,
): boolean {
  if (pkg.cancellationOptionPolicy) {
    return false
  }
  const policy = orderItem?.cancellationOptionPolicy as App.CancellationPolicyType | undefined
  return !policy ||
    pkg.roomRate?.cancellationPolicy?.type === policy ||
    !!pkg.roomRate?.soldCancellationPolicies?.includes(policy)
}

export function getBookedCancellationPolicy(
  cancellationPolicy: App.OfferCancellationPolicy | undefined,
  cancellationOptionPolicy?: string,
): App.OfferCancellationPolicy | undefined {
  if (!cancellationOptionPolicy || cancellationPolicy?.type === cancellationOptionPolicy) {
    return cancellationPolicy
  }
  return { type: cancellationOptionPolicy as App.CancellationPolicyType, policyDetail: [] }
}

export function getItemCancellationOptionPolicy(item: App.Checkout.AnyItem): App.CancellationPolicyType | undefined {
  return 'cancellationOptionPolicy' in item ? item.cancellationOptionPolicy : undefined
}

export function isSameCancellationOption(
  a: Pick<App.HotelPackage, 'cancellationOptionPolicy'>,
  b: Pick<App.HotelPackage, 'cancellationOptionPolicy'>,
): boolean {
  return (a.cancellationOptionPolicy ?? undefined) === (b.cancellationOptionPolicy ?? undefined)
}

export function payableDateChangeDifference(
  difference: number,
  orderItem?: Pick<App.OrderItem, 'cancellationOptionPolicy'>,
): number {
  return orderItem?.cancellationOptionPolicy === CANCELLATION_POLICIES.NON_REFUNDABLE ? Math.max(0, difference) : difference
}
